import React, { useState } from 'react'
import { Button, Modal, Form } from 'react-bootstrap'
import { Fund, MovementFilterLeafType, MovementFilterNode, Tag } from 'types'
import ConditionTreeEditor, { LeafNode, LeafRenderResult, TreeNode, validateTree } from 'common/components/ConditionTreeEditor'

// ─── Leaf type metadata ────────────────────────────────────────────────────────

interface LeafMeta {
    value: MovementFilterLeafType
    label: string
    inputType: 'text' | 'date' | 'number' | 'fund' | 'tag' | 'none'
    placeholder?: string
}

const LEAF_TYPES: LeafMeta[] = [
    { value: 'DATE_AFTER',         label: 'Date from (≥)',     inputType: 'date',   placeholder: 'YYYY-MM-DD' },
    { value: 'DATE_BEFORE',        label: 'Date until (≤)',    inputType: 'date',   placeholder: 'YYYY-MM-DD' },
    { value: 'TEXT',               label: 'Text (regex)',      inputType: 'text',   placeholder: '.*AMAZON.*' },
    { value: 'AMOUNT_GREATER_THAN',label: 'Amount >',          inputType: 'number', placeholder: '0' },
    { value: 'AMOUNT_LESS_THAN',   label: 'Amount <',          inputType: 'number', placeholder: '0' },
    { value: 'AMOUNT_EQUAL_TO',    label: 'Amount =',          inputType: 'number', placeholder: '42.50' },
    { value: 'FUND',               label: 'Fund',              inputType: 'fund' },
    { value: 'HAS_TAG',            label: 'Has tag',           inputType: 'tag' },
    { value: 'HAS_NO_TAGS',        label: 'Has no tags',       inputType: 'none' },
]

const defaultLeaf = (): LeafNode => ({ kind: 'leaf', type: 'TEXT', parameter: '' })
const defaultTree = (): MovementFilterNode => ({ kind: 'all', children: [defaultLeaf() as unknown as MovementFilterNode] })

const isLeafValid = (leaf: LeafNode) => {
    const meta = LEAF_TYPES.find(t => t.value === leaf.type)
    return meta?.inputType === 'none' || leaf.parameter.trim() !== ''
}

// ─── Modal ─────────────────────────────────────────────────────────────────────

interface Props {
    currentFilter: MovementFilterNode | null
    funds: Record<number, Fund> | null
    tags: Record<number, Tag> | null
    onSuccess: (result: MovementFilterNode | null) => void
}

function FilterModal({ currentFilter, funds, tags, onSuccess }: Props) {
    const [tree, setTree] = useState<MovementFilterNode>(currentFilter ?? defaultTree())
    const [showErrors, setShowErrors] = useState(false)

    const renderLeaf = (node: LeafNode, onChange: (n: LeafNode) => void, showErrors: boolean): LeafRenderResult => {
        const meta = LEAF_TYPES.find(t => t.value === node.type)!
        const isInvalid = showErrors && meta.inputType !== 'none' && node.parameter.trim() === ''

        const typeSelector = (
            <Form.Control as="select" size="sm" value={node.type}
                onChange={e => onChange({ ...node, type: e.target.value, parameter: '' } as LeafNode)}>
                {LEAF_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </Form.Control>
        )

        let parameterInput: React.ReactNode = null
        if (meta.inputType === 'fund') {
            parameterInput = (
                <Form.Control as="select" size="sm" value={node.parameter}
                    onChange={e => onChange({ ...node, parameter: e.target.value })} isInvalid={isInvalid}>
                    <option value="">Select fund…</option>
                    {funds && Object.values(funds).map(f => <option key={f.id} value={String(f.id)}>{f.name}</option>)}
                </Form.Control>
            )
        } else if (meta.inputType === 'tag') {
            parameterInput = (
                <Form.Control as="select" size="sm" value={node.parameter}
                    onChange={e => onChange({ ...node, parameter: e.target.value })} isInvalid={isInvalid}>
                    <option value="">Select tag…</option>
                    {tags && Object.values(tags).map(t => <option key={t.id} value={String(t.id)}>{t.name}</option>)}
                </Form.Control>
            )
        } else if (meta.inputType !== 'none') {
            parameterInput = (
                <Form.Control size="sm" type={meta.inputType} placeholder={meta.placeholder}
                    value={node.parameter}
                    onChange={e => onChange({ ...node, parameter: e.target.value })}
                    isInvalid={isInvalid} />
            )
        }

        return { typeSelector, parameterInput, isInvalid }
    }

    const handleApply = () => {
        if (!validateTree(tree as TreeNode, isLeafValid)) {
            setShowErrors(true)
            return
        }
        onSuccess(tree)
    }

    const handleClear = () => onSuccess(null)
    const handleClose = () => onSuccess(currentFilter)

    return (
        <Modal show={true} onHide={handleClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Filters</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form.Label className="fw-semibold">Conditions</Form.Label>
                <div className="mb-3">
                    <ConditionTreeEditor
                        node={tree as TreeNode}
                        onChange={n => setTree(n as MovementFilterNode)}
                        renderLeaf={renderLeaf}
                        defaultLeaf={defaultLeaf}
                        depth={0}
                        showErrors={showErrors}
                    />
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" onClick={handleClear}>Clear filters</Button>
                <Button variant="primary" onClick={handleApply}>Apply</Button>
            </Modal.Footer>
        </Modal>
    )
}

export default FilterModal
